import { useState, useId, type FC } from "react";
import {
  Dropdown,
  FormControl,
  HelperText,
  Label,
  type IDropdownItem,
} from "@uhg-harmony/react";

interface ValidatedDropdownProps {
  id?: string;
  label?: string;
  helperText?: string;
  items?: IDropdownItem[];
  showClearButton?: boolean;
  placeholderText?: string;
  errorMsgOrientation?: "top" | "bottom";
  disabled?: boolean;
  required?: boolean;
  value?: IDropdownItem | null;
  onChange?: (item: IDropdownItem | null) => void;
  onSelectionChange?: (item: IDropdownItem | null) => void;
  forceBlurred?: boolean;
  hideDefaultRequiredError?: boolean;
}

export const ValidatedDropdown: FC<ValidatedDropdownProps> = ({
  id,
  label,
  helperText,
  items = [],
  showClearButton = true,
  placeholderText,
  errorMsgOrientation,
  disabled = false,
  required = false,
  value: externalValue,
  onChange: externalOnChange,
  onSelectionChange,
  forceBlurred = false,
  hideDefaultRequiredError = false,
}) => {
  const [internalSelectedItem, setInternalSelectedItem] =
    useState<IDropdownItem | null>(null);
  const [blurred, setBlurred] = useState(false);

  const uniqueId = useId();
  const effectiveId = id ?? uniqueId;

  // Support both controlled and uncontrolled modes
  const isControlled = externalValue !== undefined;
  const selectedItem = isControlled ? externalValue : internalSelectedItem;
  const isBlurred = blurred || forceBlurred;

  const error =
    !hideDefaultRequiredError && required && !selectedItem && isBlurred
      ? "Folder needs to be selected"
      : "";

  const handleChange = (item: IDropdownItem | null) => {
    if (isControlled) {
      externalOnChange?.(item);
    } else {
      setInternalSelectedItem(item);
    }
    onSelectionChange?.(item);
  };

  const handleClear = () => {
    if (isControlled) {
      externalOnChange?.(null);
    } else {
      setInternalSelectedItem(null);
    }
    onSelectionChange?.(null);
  };

  return (
    <FormControl
      id={effectiveId}
      required={required}
      error={error}
      errorMsgOrientation={errorMsgOrientation}
      disabled={disabled}
    >
      <Label id={`${effectiveId}-lbl`}>
        {label ?? "Select an option"}
      </Label>
      {helperText && <HelperText>{helperText}</HelperText>}
      <Dropdown
        type="single"
        items={items}
        value={selectedItem ?? undefined}
        onChange={handleChange}
        onBlur={() => setBlurred(true)}
        onClear={handleClear}
        showClearButton={showClearButton}
        placeholderText={placeholderText ?? "Please select"}
      />
    </FormControl>
  );
};
